import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2, Send } from "lucide-react";
import { z } from "zod";
import { useToast } from "@/hooks/use-toast";
import { useIsMobile } from "@/hooks/use-mobile";
import { useMagnetic } from "@/hooks/useMagnetic";
import SectionGlow from "./SectionGlow";
import { ease, duration } from "@/lib/motion";

const contactSchema = z.object({
  name: z.string().trim().min(1, "Name is required").max(100),
  email: z.string().trim().email("Please enter a valid email").max(255),
  message: z.string().trim().min(1, "Message is required").max(2000),
});

type FormData = z.infer<typeof contactSchema>;

type ContactFormProps = {
  formData: FormData;
  errors: Partial<Record<keyof FormData, string>>;
  sending: boolean;
  submitted: boolean;
  handleChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  handleSubmit: (e: React.FormEvent) => void;
};

const ContactForm = ({ formData, errors, sending, submitted, handleChange, handleSubmit }: ContactFormProps) => {
  const submitRef = useRef<HTMLButtonElement>(null);
  useMagnetic(submitRef);

  if (submitted) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: duration.deliberate, ease: ease.smooth }}
        className="flex flex-col items-center justify-center text-center py-10"
      >
        <CheckCircle2 className="h-14 w-14 text-sage mb-5" />
        <h3 className="font-heading text-2xl leading-tight text-cream mb-2">Got it.</h3>
        <p className="text-cream/60 text-sm">I'll be in touch within 24 hours.</p>
      </motion.div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5" noValidate>
      <div>
        <label htmlFor="name" className="block text-[10px] font-semibold uppercase tracking-[0.15em] text-cream/60 mb-2 md:text-xs">Your Name</label>
        <input
          id="name"
          name="name"
          type="text"
          autoComplete="name"
          value={formData.name}
          onChange={handleChange}
          className={`w-full bg-charcoal border ${errors.name ? "border-destructive" : "border-cream/10"} rounded-lg px-4 py-3 text-sm text-cream placeholder:text-cream/60 focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/30 transition-[border-color,box-shadow] duration-150`}
          placeholder="John Smith"
        />
        {errors.name && <p role="alert" className="text-destructive text-xs mt-1">{errors.name}</p>}
      </div>
      <div>
        <label htmlFor="email" className="block text-[10px] font-semibold uppercase tracking-[0.15em] text-cream/60 mb-2 md:text-xs">Email Address</label>
        <input
          id="email"
          name="email"
          type="email"
          autoComplete="email"
          value={formData.email}
          onChange={handleChange}
          className={`w-full bg-charcoal border ${errors.email ? "border-destructive" : "border-cream/10"} rounded-lg px-4 py-3 text-sm text-cream placeholder:text-cream/60 focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/30 transition-[border-color,box-shadow] duration-150`}
          placeholder="john@business.com"
        />
        {errors.email && <p role="alert" className="text-destructive text-xs mt-1">{errors.email}</p>}
      </div>
      <div>
        <label htmlFor="message" className="block text-[10px] font-semibold uppercase tracking-[0.15em] text-cream/60 mb-2 md:text-xs">Tell us about your project</label>
        <textarea
          id="message"
          name="message"
          rows={5}
          value={formData.message}
          onChange={handleChange}
          className={`w-full bg-charcoal border ${errors.message ? "border-destructive" : "border-cream/10"} rounded-lg px-4 py-3 text-sm text-cream placeholder:text-cream/60 focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/30 transition-[border-color,box-shadow] duration-150 resize-none`}
          placeholder="What's wrong with your current site, what you want to fix, and when you need it live."
        />
        {errors.message && <p role="alert" className="text-destructive text-xs mt-1">{errors.message}</p>}
      </div>
      <button
        ref={submitRef}
        type="submit"
        disabled={sending}
        className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-primary text-primary-foreground px-6 py-3.5 text-sm font-semibold shadow-md shadow-primary/25 hover:bg-forest-light hover:-translate-y-0.5 transition-[background-color,box-shadow,transform] duration-300 hover:shadow-lg hover:shadow-primary/30 disabled:opacity-70 will-change-transform"
      >
        {sending ? "Sending..." : (<>Book My Free Consultation <Send className="h-4 w-4" /></>)}
      </button>
    </form>
  );
};

const CTASection = () => {
  const { toast } = useToast();
  const isMobile = useIsMobile();
  const [formData, setFormData] = useState<FormData>({ name: "", email: "", message: "" });
  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name as keyof FormData]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const result = contactSchema.safeParse(formData);
    if (!result.success) {
      const fieldErrors: Partial<Record<keyof FormData, string>> = {};
      result.error.issues.forEach((issue) => {
        const field = issue.path[0] as keyof FormData;
        if (!fieldErrors[field]) fieldErrors[field] = issue.message;
      });
      setErrors(fieldErrors);
      return;
    }
    setSending(true);
    const id = crypto.randomUUID();
    const { supabase } = await import("@/integrations/supabase/client");
    const { error } = await supabase.from("contact_submissions").insert({
      id,
      name: result.data.name,
      email: result.data.email,
      message: result.data.message,
    });
    if (error) {
      setSending(false);
      toast({ title: "Something went wrong", description: "Please try again or email us directly.", variant: "destructive" });
      return;
    }
    supabase.functions.invoke("send-transactional-email", {
      body: {
        templateName: "contact-confirmation",
        recipientEmail: result.data.email,
        idempotencyKey: `contact-confirm-${id}`,
        templateData: { name: result.data.name },
      },
    });
    setSending(false);
    setSubmitted(true);
    toast({ title: "Message sent!", description: "We'll get back to you within 24 hours." });
  };

  const formProps: ContactFormProps = { formData, errors, sending, submitted, handleChange, handleSubmit };

  return (
    <section
      id="contact"
      className="lg:sticky top-0 min-h-0 lg:min-h-[100dvh] flex items-center bg-charcoal relative overflow-hidden"
      style={{ zIndex: 10 }}
      data-dark-section
    >
      <SectionGlow placement="left" />

      <div className="container mx-auto relative z-10 py-16 md:py-20 lg:py-0">
        {isMobile ? (
          <motion.div
            className="space-y-8"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, amount: 0.1 }}
            transition={{ duration: duration.smooth, ease: ease.smooth }}
          >
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.25em] text-sage/90 mb-3">Get Started</p>
              <h2 className="font-heading text-3xl sm:text-4xl text-cream mb-6 font-bold leading-[1.1]">
                Ready to <span className="text-accent-sweep">Launch</span> in 7 Days.
              </h2>
              <p className="text-cream/60 leading-relaxed mb-8 max-w-md">
                Raleigh, Durham, and all of NC. Tell us about your project — we respond within 24 hours.
              </p>
              <div className="space-y-3 text-sm text-cream/60">
                <p className="flex items-center gap-2.5"><ArrowRight className="h-3.5 w-3.5 text-sage" /> 7-day delivery — most agencies take 6–8 weeks</p>
                <p className="flex items-center gap-2.5"><ArrowRight className="h-3.5 w-3.5 text-sage" /> Free consultation. No pressure, no obligation.</p>
                <p className="flex items-center gap-2.5"><ArrowRight className="h-3.5 w-3.5 text-sage" /> 100% satisfaction guarantee on every project</p>
              </div>
              <div className="mt-6 pt-6 border-t border-cream/5 space-y-2 text-sm">
                <p className="text-cream/50 text-xs uppercase tracking-[0.15em] font-semibold mb-2">Prefer to reach out directly?</p>
                <p><a href="tel:8287472665" className="text-cream/80 hover:text-cream transition-colors">(828) 747-2665</a></p>
                <p><a href="mailto:nick@greenleafdigital.net" className="text-cream/80 hover:text-cream transition-colors break-all">nick@greenleafdigital.net</a></p>
              </div>
            </div>

            <div className="bg-charcoal-light/50 rounded-xl border border-cream/5 p-6">
              <ContactForm {...formProps} />
            </div>
          </motion.div>
        ) : (
          <div className="grid lg:grid-cols-2 gap-10 md:gap-20 items-center">
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{ duration: duration.smooth, ease: ease.smooth }}
            >
              <p className="text-xs font-semibold uppercase tracking-[0.25em] text-sage/90 mb-3 md:text-sm">Get Started</p>
              <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl lg:text-[3.5rem] text-cream mb-6 font-bold leading-[1.1]">
                Ready to <span className="text-accent-sweep">Launch</span> in 7 Days.
              </h2>
              <p className="text-cream/60 leading-relaxed mb-8 md:text-lg max-w-md">
                Raleigh, Durham, and all of NC. Tell us about your project — we respond within 24 hours.
              </p>
              <div className="space-y-3 text-sm text-cream/60">
                <p className="flex items-center gap-2.5"><ArrowRight className="h-3.5 w-3.5 text-sage" /> 7-day delivery — most agencies take 6–8 weeks</p>
                <p className="flex items-center gap-2.5"><ArrowRight className="h-3.5 w-3.5 text-sage" /> Free consultation. No pressure, no obligation.</p>
                <p className="flex items-center gap-2.5"><ArrowRight className="h-3.5 w-3.5 text-sage" /> 100% satisfaction guarantee on every project</p>
              </div>
              <div className="mt-6 pt-6 border-t border-cream/5 space-y-2 text-sm">
                <p className="text-cream/50 text-xs uppercase tracking-[0.15em] font-semibold mb-2">Prefer to reach out directly?</p>
                <p><a href="tel:8287472665" className="text-cream/80 hover:text-cream transition-colors">(828) 747-2665</a></p>
                <p><a href="mailto:nick@greenleafdigital.net" className="text-cream/80 hover:text-cream transition-colors break-all">nick@greenleafdigital.net</a></p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: duration.smooth, delay: 0.12, ease: ease.smooth }}
              className="bg-charcoal-light/50 rounded-xl border border-cream/5 p-6 md:p-10"
            >
              <ContactForm {...formProps} />
            </motion.div>
          </div>
        )}
      </div>
    </section>
  );
};

export default CTASection;
